/* import __COLOCATED_TEMPLATE__ from './company-profile.hbs'; */
import Component from '@glimmer/component';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import { inject as service } from '@ember/service';
import { task } from "ember-concurrency";
import { tracked } from '@glimmer/tracking';
import { action, getProperties } from '@ember/object';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { APPLICATION } from 'sh-www/models/application';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import { showModal } from 'sh-www/utils/modal-controls';

const POSTINGS_PAGE_SIZE = 5;
const CONTACTS_PAGE_SIZE = 10;

const POSTINGS_INCLUDES = 'applications,assignment-deliverables,assignment-schedules,assignment-user-meta,brand,buyer.company,candidate-location,location,services';

class CompanyProfileComponent extends Component {
  @service('commands.user-commands') userCommandService;
  @service('commands.application-commands') applicationCommandService;
  @service('commands.assignment-commands') assignmentCommandService;
  @service errorTracker;
  @service session;
  @service shData;
  @service store;

  @tracked contacts = [];

  #currentCompanyConnection = createCache(() => {
    return this.args.user.companySellerConnections.find(({ companyId }) => this.args.company.id == companyId);
  });

  get currentCompanyConnection() {
    return getValue(this.#currentCompanyConnection);
  }

  get activeProjectsCount() {
    return this.currentCompanyConnection?.activeProjects ?? 0;
  }

  get completedProjectsCount() {
    return this.currentCompanyConnection?.completedProjects ?? 0;
  }

  @action
  bootstrap(user, company) {
    this.loadActiveApplications.perform(user, company, 1);
    this.loadCompletedApplications.perform(-1, user, company, 1);
    this.loadPostings.perform(company, 1);
    this.loadContacts.perform(user, company, 1);
  }

  @action
  onContactClicked(contact) {
    showModal(this, 'lsg/lsg-send-message', {
      userToSendMessageTo: contact,
    });
  }

  @task
  *loadActiveApplications(user, company, currentPage) {
    try {
      const response = yield this.applicationCommandService.loadApplications(
        undefined,
        APPLICATION.SIMPLE_STATUS.IN_PROGRESS,
        undefined,
        false,
        undefined,
        currentPage,
        10,
        undefined,
        'assignment,thread.messages,pitch,brand',
        user.id,
        company.id,
        null
      );
      return response;
    } catch(error) {
      handleErrorResponseWithModal(
        this,
        error,
        "There was a problem loading projects. Please try again or contact support if the problem continues.",
        `loadActiveApplications -- error loading applications for user id: ${user.id}`,
      );
    }
  }

  @task
  *loadCompletedApplications(activeServiceFilterId, user, company, currentPage) {
    const serviceFilter = activeServiceFilterId > 0 ? activeServiceFilterId : null;

    try {
      const response = yield this.applicationCommandService.loadApplications(
        undefined,
        APPLICATION.SIMPLE_STATUS.COMPLETE,
        undefined,
        false,
        undefined,
        currentPage,
        5,
        undefined,
        'application-seller-reviews,seller-feedback,assignment,services,projectdigest.review.reviewer,projectdigest.company,' +
        'projectdigest.oembed,projectdigest.projectdigest.projectdigest-verifications:verified_or_latest,projectdigest.services,' +
        'projectdigest.classifications',
        user.id,
        company.id,
        serviceFilter,
        true
      );
      return response;
    } catch(error) {
      handleErrorResponseWithModal(
        this,
        error,
        "There was a problem loading projects. Please try again or contact support if the problem continues.",
        `loadCompletedApplications -- error loading applications for user id: ${user.id}`,
      );
    }
  }

  @task
  *loadPostings(company, page) {
    const filter = { company: company.id, status: ASSIGNMENT.STATUS.LIVE };
    const params = {
      filter,
      include: POSTINGS_INCLUDES,
      page,
      count: POSTINGS_PAGE_SIZE,
      format: ASSIGNMENT.FORMATS.SELLER_PAGINATED,
    };

    try {
      const postings = yield this.store.query('assignment', params);
      this.shData.pushResponseToStore(postings);
      const pagination = this.shData.getPaginationModelFromResponse(postings);
      return { postings, pagination };
    } catch (e) {
      handleErrorResponseWithModal(
        this,
        error,
        "There was a problem loading postings. Please try again or contact support if the problem continues.",
        `loadPostings -- error loading postings for company id: ${company.id}`,
      );
    }
  }

  @task
  *loadContacts(seller, company) {
    try {
      const contactsMap = new Map();
      let contactPagination = null;

      while (!contactPagination || (contactPagination.currentPage < contactPagination.totalPages)) {
        const response = yield this.applicationCommandService.loadApplications(
          undefined,
          APPLICATION.SIMPLE_STATUS.HIRED,
          undefined,
          undefined,
          undefined,
          (contactPagination?.currentPage ?? 0) + 1,
          CONTACTS_PAGE_SIZE,
          undefined,
          'buyer,followers',
          seller.id,
          company.id,
        );
        const { applications, pagination } = response;
        const paginationClone = getProperties(pagination, 'currentPage', 'totalPages');
        contactPagination = paginationClone;

        const buyers = applications.map(({ buyer }) => buyer);
        const followers = applications.map(({ followers }) => followers.toArray()).flat();
        const followersAndBuyers = [...buyers, ...followers];

        followersAndBuyers.forEach(contact => contactsMap.set(contact.id, contact));
        this.contacts = [...contactsMap.values()];
      }
    } catch (e) {
      this.errorTracker.error('loadContacts -- Could not load company contacts', this, e);
      throw e;
    }
  }
}

export default CompanyProfileComponent;
